import { useState } from "react";
import { FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { BIO_TEMPLATES, bioTemplateToHtml, stripBioHtml, type BioProfileFacts, type BioTemplate } from "@/content/bioTemplates";

interface BioTemplatePickerProps {
  /** Current bio HTML — used to decide whether picking a template needs a replace confirmation. */
  currentBio: string;
  /** Called with the chosen template as editor HTML. */
  onApply: (html: string) => void;
  /** Profile details used to pre-fill the template's placeholders. */
  facts?: BioProfileFacts;
}

/**
 * "Use a sample bio" button + menu for the teacher profile Professional Bio field.
 */
export function BioTemplatePicker({ currentBio, onApply, facts }: BioTemplatePickerProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [pending, setPending] = useState<BioTemplate | null>(null);


  const hasExistingText = stripBioHtml(currentBio).trim().length > 0;

  const pick = (template: BioTemplate) => {
    setMenuOpen(false);
    if (hasExistingText) {
      setPending(template);
    } else {
      onApply(bioTemplateToHtml(template, facts));
    }
  };

  return (
    <>
      <Popover open={menuOpen} onOpenChange={setMenuOpen}>
        <PopoverTrigger asChild>
          <Button type="button" variant="ghost" size="sm" className="gap-1.5 text-xs h-7 px-2">
            <FileText className="w-3.5 h-3.5" />
            Use a sample bio
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-72 p-1" align="start">
          <p className="px-2 pt-1.5 pb-1 text-xs text-muted-foreground">
            Pick a starting point, then replace the [bracketed] text.
          </p>
          {BIO_TEMPLATES.map((t) => (
            <button
              key={t.name}
              type="button"
              onClick={() => pick(t)}
              className="w-full text-left rounded-md px-2 py-1.5 hover:bg-accent focus:bg-accent focus:outline-none transition-colors cursor-pointer"
            >
              <span className="block text-sm font-medium text-foreground">{t.name}</span>
              <span className="block text-xs text-muted-foreground">{t.description}</span>
            </button>
          ))}
        </PopoverContent>
      </Popover>

      <AlertDialog open={pending !== null} onOpenChange={(open) => { if (!open) setPending(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Replace your bio?</AlertDialogTitle>
            <AlertDialogDescription>
              Using the &ldquo;{pending?.name}&rdquo; sample will replace the text you have already written.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep my bio</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (pending) onApply(bioTemplateToHtml(pending, facts));
                setPending(null);
              }}
            >
              Replace bio
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
